// Shared utilities and types for Explorer tabs

import i18n from "../../i18n";
import { formatChileanDateWithWeekday } from "../../utils/dateUtils";

/** livestream_channels document ids — 24-char hex ObjectId strings */
export function isMongoObjectIdString(s: string | null | undefined): boolean {
  return typeof s === "string" && /^[a-fA-F0-9]{24}$/.test(s.trim());
}

/** Program ranking row with no livestream_programs assignment → /livestreams/program/none */
export function isUnassignedExplorerProgramRankingRow(row: {
  key?: string;
  programId?: string | null;
  programName?: string | null;
}): boolean {
  if (row.programId != null && String(row.programId).trim() !== "") {
    return false;
  }
  const key = row.key?.trim();
  if (key === "__sin_programa__") return true;
  if (key?.toLowerCase() === "name:sin programa") return true;
  return row.programName?.trim().toLowerCase() === "sin programa";
}

export type LivestreamDetailSummaryStream = {
  _id?: string;
  livestreamId?: string;
  peakViewers?: number;
  averageViewers?: number;
  totalViews?: number;
  viewIncrement?: number | null;
  youtubeDuration?: number;
  totalDuration?: number;
  actualStartTime?: string;
  actualEndTime?: string;
};

/** Period summary from livestreams shown in Explorer channel/program detail tables. */
export function computeLivestreamDetailSummaryFromStreams(
  rows: LivestreamDetailSummaryStream[],
): {
  episodeCount: number;
  avgPeakViewers: number;
  avgViewers: number;
  totalDurationMinutes: number;
  totalViews: number;
  viewsPerStream: number;
} {
  const n = rows.length;
  if (n === 0) {
    return {
      episodeCount: 0,
      avgPeakViewers: 0,
      avgViewers: 0,
      totalDurationMinutes: 0,
      totalViews: 0,
      viewsPerStream: 0,
    };
  }
  let totalDurationMinutes = 0;
  let sumPeak = 0;
  let sumAvg = 0;
  let totalViews = 0;
  const countedVideo = new Set<string>();
  for (const ls of rows) {
    sumPeak += Number(ls.peakViewers) || 0;
    sumAvg += Number(ls.averageViewers) || 0;
    const videoKey = String(ls.livestreamId ?? ls._id ?? "").trim();
    if (!videoKey || !countedVideo.has(videoKey)) {
      if (videoKey) countedVideo.add(videoKey);
      totalViews += Number(ls.totalViews) || 0;
    }
    const yt = ls.youtubeDuration;
    const td = ls.totalDuration;
    let mins = 0;
    if (yt != null && yt > 0) mins = yt / 60;
    else if (td != null && td > 0) mins = td / 60;
    else if (ls.actualStartTime && ls.actualEndTime) {
      const d =
        (new Date(ls.actualEndTime).getTime() -
          new Date(ls.actualStartTime).getTime()) /
        60000;
      mins = Number.isFinite(d) ? Math.max(0, d) : 0;
    }
    totalDurationMinutes += mins;
  }
  return {
    episodeCount: n,
    avgPeakViewers: Math.round(sumPeak / n),
    avgViewers: Math.round(sumAvg / n),
    totalDurationMinutes: Math.round(totalDurationMinutes),
    totalViews,
    viewsPerStream: Math.round(totalViews / n),
  };
}

/** Resumen del período — livestream channel/program detail summary cards. */
export const LIVESTREAM_DETAIL_SUMMARY_TOOLTIPS = {
  get viewsTotales() {
    return i18n.t("explorer:tooltip.viewsTotales");
  },
  get viewsProm() {
    return i18n.t("explorer:tooltip.viewsProm");
  },
  get livestreams() {
    return i18n.t("explorer:tooltip.livestreamCount");
  },
  get avgEsp() {
    return i18n.t("explorer:tooltip.avgEsp");
  },
  get avgPeakEsp() {
    return i18n.t("explorer:tooltip.avgPeakEsp");
  },
  get horasTotales() {
    return i18n.t("explorer:tooltip.horasTotales");
  },
};

/** User-facing livestream metric abbreviations (Spanish PROM. for average). */
export const LIVESTREAM_METRIC_LABELS = {
  get avgEsp() {
    return i18n.t("explorer:metricAvgViewers");
  },
  get nowEsp() {
    return i18n.t("explorer:metricNowViewers");
  },
  get avgPeakEsp() {
    return i18n.t("explorer:metricAvgPeakViewers");
  },
};

/**
 * Positional accents for Resumen del período values and Tendencias chart lines:
 * the Nth card in either section uses the Nth color, on every Explorer page.
 */
export const EXPLORER_ACCENT_SEQUENCE = [
  "#1976d2",
  "#ed6c02",
  "#2e7d32",
  "#9c27b0",
  "#d32f2f",
  "#00897b",
] as const;

/** Extra qualitative colors after the six explorer accents, for crowded line charts. */
const CHART_LINE_EXTRAS = [
  "#f1c40f",
  "#e91e63",
  "#00bcd4",
  "#8bc34a",
  "#ff5722",
  "#3f51b5",
  "#cddc39",
  "#795548",
  "#00acc1",
  "#ab47bc",
  "#66bb6a",
  "#ff7043",
  "#5c6bc0",
  "#26a69a",
  "#ec407a",
  "#9ccc65",
  "#29b6f6",
  "#ffa726",
  "#7e57c2",
  "#ef5350",
  "#26c6da",
  "#d4e157",
  "#8d6e63",
  "#42a5f5",
] as const;

const CHART_LINE_SEQUENCE = [...EXPLORER_ACCENT_SEQUENCE, ...CHART_LINE_EXTRAS];

export function explorerAccentAt(index: number): string {
  return EXPLORER_ACCENT_SEQUENCE[index % EXPLORER_ACCENT_SEQUENCE.length]!;
}

function hslToHex(hue: number, sat: number, light: number): string {
  const s = sat / 100;
  const l = light / 100;
  const a = s * Math.min(l, 1 - l);
  const channel = (n: number) => {
    const k = (n + hue / 30) % 12;
    const value = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * value)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${channel(0)}${channel(8)}${channel(4)}`;
}

function extraChartLineAccent(index: number): string {
  const hue = (index * 137.508) % 360;
  const sat = 74;
  const light = index % 2 === 0 ? 58 : 46;
  return hslToHex(hue, sat, light);
}

/** Unique chart colors. Uses a qualitative palette so neighboring lines do not look the same. */
export function distinctExplorerAccents(count: number): string[] {
  const size = Math.max(0, Math.floor(count));
  if (size <= CHART_LINE_SEQUENCE.length) {
    return CHART_LINE_SEQUENCE.slice(0, size);
  }
  return [
    ...CHART_LINE_SEQUENCE,
    ...Array.from({ length: size - CHART_LINE_SEQUENCE.length }, (_, index) =>
      extraChartLineAccent(CHART_LINE_SEQUENCE.length + index),
    ),
  ];
}

/** Stable accent for the same program id across livestream rows and charts. */
export function explorerAccentForKey(key: string): string {
  const value = key.trim();
  if (!value) return explorerAccentAt(0);
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (Math.imul(hash, 33) + value.charCodeAt(i)) >>> 0;
  }
  return explorerAccentAt(hash);
}

export function programWindowAccent(
  window: { id?: string | null; name?: string | null },
  index: number,
): string {
  return explorerAccentAt(index);
}

/** Distinct palette colors for each program in one chart, in appearance order. */
export function programWindowChartAccents(
  windows: Array<{ id?: string | null; name?: string | null }>,
): string[] {
  const keys = windows.map(
    (window, index) =>
      [window.name, window.id]
        .map((value) => String(value ?? "").trim())
        .find(Boolean) || `window-${index}`,
  );
  const firstIndex = new Map<string, number>();
  for (const key of keys) {
    if (!firstIndex.has(key)) firstIndex.set(key, firstIndex.size);
  }
  const palette = distinctExplorerAccents(firstIndex.size);
  return keys.map((key) => palette[firstIndex.get(key) ?? 0] ?? explorerAccentAt(0));
}

export const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

/** Ranking/compare table compact counts. K keeps two decimals so close rows stay comparable. */
export const formatTableNumber = (num: number): string => {
  if (num >= 1_000_000) {
    return `${Math.round(num / 1_000_000).toLocaleString("es-CL")}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toLocaleString("es-CL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}K`;
  }
  return Math.round(num).toLocaleString("es-CL");
};

/** Investment points for Explorer rankings (e.g. 1.250 pts). */
export const formatInvestmentPoints = (num: number): string =>
  `${Math.round(Number(num) || 0).toLocaleString("es-CL")} pts`;

/** CLP investment for Explorer rankings (e.g. $1.500.000). */
export const formatInvestmentClp = (num: number): string =>
  `$${Math.round(Number(num) || 0).toLocaleString("es-CL")}`;

/** Company's CLP value per point, or null when points are shown without CLP. */
export function getExplorerClpPerPoint(
  company:
    | { explorerBrandInvestment?: { clpPerPoint?: number | null } | null }
    | null
    | undefined,
): number | null {
  const n = Number(company?.explorerBrandInvestment?.clpPerPoint);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export const investmentColumnLabel = (clpPerPoint: number | null): string =>
  clpPerPoint
    ? i18n.t("explorer:investment")
    : i18n.t("explorer:investmentPts");

/**
 * Ranking search: typed text filters the table. Picked options replace that
 * text filter so the dropdown can stay open while looking for the next row.
 */
export function explorerTextOrIdsFilter(
  debouncedQuery: string,
  selectedIds: readonly string[],
): { search?: string; ids?: string[] } {
  if (selectedIds.length > 0) return { ids: [...selectedIds] };
  const search = debouncedQuery.trim();
  return search ? { search } : {};
}

/** Brand category label for Explorer filters and rows. */
export function brandCategoryLabel(
  category: string | null | undefined,
): string | undefined {
  if (!category) return undefined;
  const key = category.toLowerCase();
  const translated = i18n.t(`explorer:brandCategory.${key}`, {
    defaultValue: "",
  });
  return translated || category;
}

export function explorerPaginationLabel(
  from: number,
  to: number,
  count: number,
): string {
  const shown =
    count !== -1
      ? String(count)
      : i18n.t("explorer:pagination.moreThan", { to });
  return i18n.t("explorer:pagination.range", { from, to, shown });
}

export function explorerWeekdayFull(dayIndex: number): string {
  return i18n.t(`explorer:weekday.full.${dayIndex}`);
}

export function explorerWeekdayShort(dayIndex: number): string {
  return i18n.t(`explorer:weekday.short.${dayIndex}`);
}

/** Company has Explorer brand investment estimates enabled with valid points. */
export function isExplorerBrandInvestmentEnabled(
  company:
    | {
        explorerBrandInvestment?: {
          enabled?: boolean;
          points?: {
            credits?: number;
            content?: number;
            banner?: number;
            placement?: number;
          };
        } | null;
      }
    | null
    | undefined,
): boolean {
  const inv = company?.explorerBrandInvestment;
  if (!inv?.enabled || !inv.points) return false;
  const { credits, content, banner, placement } = inv.points;
  return [credits, content, banner, placement].every(
    (n) => typeof n === "number" && Number.isFinite(n) && n >= 0,
  );
}

/** Like formatNumber but truncates toward −∞ (floor for non-negative inputs). */
export const formatNumberFloor = (num: number): string => {
  if (num >= 1000000) return `${Math.floor((num / 1000000) * 10) / 10}M`;
  if (num >= 1000) return `${Math.floor((num / 1000) * 10) / 10}K`;
  return `${Math.floor(num)}`;
};

/** Floored integer viewers for tables (no K/M; locale separators). */
export const formatViewerIntegerFloor = (num: number): string =>
  Math.floor(Number(num) || 0).toLocaleString("es-CL");

/** Explorer detail tables — date line in Chilean time (e.g. "1 jun 2026"). */
export function formatExplorerTableDateLine(
  dateString?: string | null,
): string {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-CL", {
    timeZone: "America/Santiago",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Explorer detail tables — time line in Chilean time (e.g. "10:46 am"). */
export function formatExplorerTableTimeLine(
  dateString?: string | null,
): string {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";
  return date
    .toLocaleTimeString("en-US", {
      timeZone: "America/Santiago",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
}

/** Format seconds as "1h 30m" or "58m". */
export function formatExplorerDurationSeconds(
  totalSeconds?: number | null,
): string {
  if (totalSeconds == null || totalSeconds <= 0) return "—";
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

/** Total livestream airtime from summed minutes (e.g. ranking Duración column). */
export const formatLivestreamDurationMinutes = (minutes: number): string => {
  const m = Math.round(Number(minutes) || 0);
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${m}m`;
};

/** Ranking table header: "Programa (698)". Omits the count until it is known. */
export function explorerColumnWithCount(
  label: string,
  count: number | null | undefined,
): string {
  if (count == null || !Number.isFinite(count)) return label;
  return `${label} (${Math.trunc(count).toLocaleString("es-CL")})`;
}

/** Shown when latest dailyMetrics has no YouTube VOD views yet. */
export function livestreamViewsUnavailableLabel(): string {
  return i18n.t("explorer:viewsUnavailable");
}

/** @deprecated Prefer livestreamViewsUnavailableLabel(); kept for existing imports. */
export const LIVESTREAM_VIEWS_UNAVAILABLE_LABEL = "No disponible";

/** Per-stream or table cell: count + "views", or unavailable label when 0. */
export function formatLivestreamOnDemandViews(views?: number | null): string {
  const v = Number(views) || 0;
  if (v <= 0) return livestreamViewsUnavailableLabel();
  return `${formatNumber(v)} views`;
}

/** Summary / ranking numeric column without "views" suffix. */
export function formatLivestreamOnDemandViewsCount(
  views?: number | null,
): string {
  const v = Number(views) || 0;
  if (v <= 0) return livestreamViewsUnavailableLabel();
  return formatNumber(v);
}

/** Explorer livestreams ranking — em dash when no VOD views; abbreviated count otherwise. */
export function formatLivestreamRankingViewsCount(
  views?: number | null,
): string {
  const v = Number(views) || 0;
  if (v <= 0) return "—";
  return formatNumber(v);
}

/** Livestream detail table metric cells — floored integer, no K/M abbreviations. */
export function formatLivestreamTableViewsCount(views?: number | null): string {
  const v = Number(views) || 0;
  if (v <= 0) return "—";
  return formatViewerIntegerFloor(v);
}

/** Tooltip for Explorer livestream row Views from latest dailyMetrics snapshot. */
export function livestreamViewsTooltip(
  collectedAt?: string | null,
  views?: number | null,
): string {
  const v = Number(views) || 0;
  if (v <= 0) return i18n.t("explorer:viewsUnavailableTooltip");

  const base = i18n.t("explorer:viewsSnapshotBase");
  if (!collectedAt) return base;
  const d = new Date(collectedAt);
  if (Number.isNaN(d.getTime())) return base;
  const when = d.toLocaleDateString("es-CL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return i18n.t("explorer:snapshotLine", { base, when });
}

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return i18n.t("explorer:today");
  if (diffDays === 1) return i18n.t("explorer:yesterday");
  if (diffDays < 7) return i18n.t("explorer:daysAgo", { count: diffDays });
  if (diffDays < 30) {
    return i18n.t("explorer:weeksAgo", { count: Math.floor(diffDays / 7) });
  }
  return i18n.t("explorer:monthsAgo", { count: Math.floor(diffDays / 30) });
};

/** X-axis tick for trend charts: D/M in Chilean calendar (UTC parts match backend period starts). */
export function formatTrendChartAxisDate(dateString: string): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  return `${date.getUTCDate()}/${date.getUTCMonth() + 1}`;
}

/** Tooltip period label; daily granularity includes weekday name (e.g. Miércoles 21 Oct). */
export function formatTrendChartTooltipPeriodLabel(
  dateString: string,
  granularityTooltip: string,
): string {
  if (!dateString) return granularityTooltip;
  const axisDate = formatTrendChartAxisDate(dateString);

  if (
    granularityTooltip === "Día" ||
    granularityTooltip === i18n.t("explorer:granularity.day")
  ) {
    return formatChileanDateWithWeekday(dateString) || `${axisDate}`;
  }

  return `${granularityTooltip} ${axisDate}`;
}

export const calculateMedian = (values: number[]): number => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]!
    : (sorted[mid - 1]! + sorted[mid]!) / 2;
};

// Cache times - 12 hours for standard explorer queries (repeat visits use cache)
export const STALE_TIME = 12 * 60 * 60 * 1000; // 12 hours
export const GC_TIME = 12 * 60 * 60 * 1000; // 12 hours

/** Bump when grouped-by-channel filtering/semantics change — invalidates React Query cache for livestreams tab */
export const EXPLORER_LIVESTREAMS_QUERY_REVISION = 17;

/** Bump when program weekly movement ranking semantics change. */
export const EXPLORER_PROGRAM_WEEKLY_MOVEMENTS_QUERY_REVISION = 4;

/** Hour-of-day and weekday (Mon=0 … Sun=6) in America/Santiago — for livestream comparador filters */
export function getAmericaSantiagoHourAndDayIndex(date: Date | string): {
  hour: number;
  dayIndexMon0: number;
} {
  const d = typeof date === "string" ? new Date(date) : date;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Santiago",
    hour: "numeric",
    hour12: false,
    weekday: "short",
  }).formatToParts(d);
  const hour = parseInt(parts.find((p) => p.type === "hour")?.value ?? "0", 10);
  const wd = parts.find((p) => p.type === "weekday")?.value ?? "Mon";
  const dayIndexMap: Record<string, number> = {
    Mon: 0,
    Tue: 1,
    Wed: 2,
    Thu: 3,
    Fri: 4,
    Sat: 5,
    Sun: 6,
  };
  const dayIndexMon0 = dayIndexMap[wd] ?? 0;
  return { hour, dayIndexMon0 };
}

export type LivestreamHourBlockId =
  | "all"
  | "madrugada"
  | "manana"
  | "tarde"
  | "prime";

export const LIVESTREAM_HOUR_BLOCKS: Array<{
  id: LivestreamHourBlockId;
  label: string;
  /** Inclusive start hour 0–23 */
  start: number;
  /** Exclusive end hour 1–24; 24 means through 23 */
  end: number;
}> = [
  { id: "all" as const, start: 0, end: 24 },
  { id: "madrugada" as const, start: 0, end: 6 },
  { id: "manana" as const, start: 6, end: 12 },
  { id: "tarde" as const, start: 12, end: 18 },
  { id: "prime" as const, start: 18, end: 24 },
].map((block) => ({
  ...block,
  get label() {
    return i18n.t(`explorer:hourBlock.${block.id}`);
  },
}));

export const LIVESTREAM_WEEKDAY_LABELS_SHORT: readonly string[] = new Proxy(
  ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"],
  {
    get(target, prop, receiver) {
      if (typeof prop === "string" && /^\d$/.test(prop)) {
        return i18n.t(`explorer:weekday.short.${prop}`);
      }
      return Reflect.get(target, prop, receiver);
    },
  },
);

/** Channel detail, viewer chart, and episode metrics. */
export const LIVESTREAM_DETAIL_STALE_TIME = 10 * 60 * 1000;
export const LIVESTREAM_DETAIL_GC_TIME = 10 * 60 * 1000;

// Live streams poll every minute. Viewer counts are already stored at that cadence.
export const LIVE_STALE_TIME = 60 * 1000;
export const LIVE_GC_TIME = 60 * 1000;

/** Audiencia grouped-channels query — aligned with Explorer tab cache TTL */
export const AUDIENCIA_STALE_TIME = STALE_TIME;
export const AUDIENCIA_GC_TIME = GC_TIME;

/** Wait after Desde/Hasta changes before fetching audiencia APIs (avoid spamming while adjusting dates). */
export const AUDIENCIA_FILTER_DEBOUNCE_MS = 350;

// Types
export type ProgramSortColumn =
  | "name"
  | "contentCount"
  | "totalViews"
  | "viewsPerEp"
  | "totalInteractions"
  | "engagementRate"
  | "uniquePaidBrandsCount"
  | "estimatedInvestment";
export type CanalesSortColumn =
  | "title"
  | "programCount"
  | "contentCount"
  | "totalViews"
  | "viewsPerEp"
  | "uniquePaidBrandsCount"
  | "estimatedInvestment";
export type SocialPlatform = "facebook" | "tiktok" | "instagram" | "youtubeShorts";
export type SocialPlatformFilter = SocialPlatform | "all";

/** Short-form sponsor implementation. `all` keeps tagged, overlay, and placement. */
export const SOCIAL_CLIP_PLACEMENT_OPTIONS = ["tagged", "overlay", "placement"] as const;
export type SocialClipPlacementType = (typeof SOCIAL_CLIP_PLACEMENT_OPTIONS)[number];
export type SocialClipPlacementFilter = SocialClipPlacementType | "all";

export function normalizeSocialClipPlacement(value: unknown): SocialClipPlacementFilter {
  if (value === "tagged" || value === "overlay" || value === "placement") return value;
  return "all";
}
export type SocialMetric = "posts" | "views" | "interactions";
export type SocialSortColumn =
  | "name"
  | "posts"
  | "views"
  | "viewsPerPost"
  | "interactions"
  | "engagement";
export type SocialPostSortColumn = "publishedAt" | "views" | "interactions" | "title";
export const SOCIAL_PLATFORMS: SocialPlatform[] = [
  "facebook",
  "tiktok",
  "instagram",
  "youtubeShorts",
];
export const SOCIAL_METRICS: SocialMetric[] = ["posts", "views", "interactions"];
export const SOCIAL_LIST_SORT_COLUMNS: Array<Exclude<SocialSortColumn, "name">> = [
  "posts",
  "views",
  "viewsPerPost",
  "interactions",
  "engagement",
];
export type SocialBrandSortColumn =
  | "name"
  | "clips"
  | "channels"
  | "views"
  | "viewsPerClip"
  | "interactions";
export const SOCIAL_BRAND_LIST_SORT_COLUMNS: Array<Exclude<SocialBrandSortColumn, "name">> = [
  "clips",
  "channels",
  "views",
  "viewsPerClip",
  "interactions",
];
export type BrandSortColumn =
  | "name"
  | "totalPaidMentions"
  | "uniqueProgramCount"
  | "uniqueChannelCount"
  | "totalViews"
  | "estimatedInvestment";
export type LivestreamSortColumn =
  | "channelName"
  | "programName"
  | "episodeCount"
  | "avgViewers"
  | "avgPeakViewers"
  | "maxPeakViewers"
  | "totalViews"
  | "totalDurationMinutes"
  | "totalLikes";
export type LivestreamDetailSortColumn =
  | "fecha"
  | "title"
  | "peak"
  | "duration"
  | "avg"
  | "views";

/** Explorer livestreams overview: table rows are either one row per channel or aggregated per program */
export type LivestreamRankingMode = "channels" | "programs";
export type SortDirection = "asc" | "desc";

const RANKING_CELL =
  "& > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root:not([colspan]):not(.MuiTableCell-alignRight), & > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root:not([colspan]):not(.MuiTableCell-alignRight)";
const RANKING_RANK_CELL =
  "& > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root:first-of-type:not([colspan]), & > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root:first-of-type:not([colspan])";
const RANKING_ICON_CELL =
  "& > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-alignCenter:not(:first-of-type):not([colspan]), & > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-alignCenter:not(:first-of-type):not([colspan])";
const RANKING_NAME_CELL =
  "& > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root:nth-of-type(2), & > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root:nth-of-type(2):not([colspan])";

/**
 * Fixed width of right-aligned data columns in every Explorer table (applied in ExplorerRoute),
 * so columns don't shift between skeletons and loaded values or across pages.
 * Columns whose header is wider keep the header width, which is also constant.
 */
export const EXPLORER_TABLE_DATA_COL_WIDTH = 104;
export const EXPLORER_TABLE_RANK_COL_WIDTH = 60;
/** Icon-only columns (e.g. favorite button). */
export const EXPLORER_TABLE_ICON_COL_WIDTH = 56;

/** Ranking tables: fixed-width rank, icon and data columns; the name column (2nd, after #) takes the rest. */
export const EXPLORER_RANKING_TABLE_SX = {
  [RANKING_CELL]: { width: "1%", whiteSpace: "nowrap" },
  [RANKING_ICON_CELL]: {
    width: EXPLORER_TABLE_ICON_COL_WIDTH,
    minWidth: EXPLORER_TABLE_ICON_COL_WIDTH,
  },
  [RANKING_RANK_CELL]: {
    width: EXPLORER_TABLE_RANK_COL_WIDTH,
    minWidth: EXPLORER_TABLE_RANK_COL_WIDTH,
  },
  [RANKING_NAME_CELL]: { width: "100%", minWidth: 200, whiteSpace: "normal" },
} as const;

/** Fixed width for a secondary left-aligned text column (e.g. Canal in the livestreams program ranking). */
export function explorerRankingFixedColSx(nth: number, width: number) {
  const cell = `.MuiTableCell-root:nth-of-type(${nth}):not([colspan])`;
  const head = `& > .MuiTableHead-root > .MuiTableRow-root > ${cell}`;
  const body = `& > .MuiTableBody-root > .MuiTableRow-root > ${cell}`;
  return {
    [`${head}, ${body}`]: { width, minWidth: width, maxWidth: width },
    // Bounding the content keeps long (noWrap) text from widening the column in auto table layout.
    [`${body} > *`]: { maxWidth: width - 32, overflow: "hidden" },
  };
}

/** Right-aligned ranking header — keeps sort arrow visible to the left of label text. */
export const RIGHT_TABLE_SORT_LABEL_SX = {
  flexDirection: "row-reverse" as const,
  "& .MuiTableSortLabel-icon": {
    marginRight: "4px",
    marginLeft: 0,
  },
};

// Special value for "all categories" filter
export const CONTENT_CATEGORY_ALL = "__all__";

/** Resolves multi-select onChange for Explorer ranking category filter (Todas + real categories). */
export function resolveContentCategoryFilter(
  prev: string[],
  next: string[],
): string[] {
  const wasAll = prev.includes(CONTENT_CATEGORY_ALL);
  const hasAll = next.includes(CONTENT_CATEGORY_ALL);
  const realCats = next.filter((c) => c !== CONTENT_CATEGORY_ALL);

  if (hasAll && !wasAll) {
    return [CONTENT_CATEGORY_ALL];
  }
  if (hasAll && wasAll && realCats.length > 0) {
    return realCats;
  }
  if (realCats.length === 0) {
    return [CONTENT_CATEGORY_ALL];
  }
  return realCats;
}

/** Explorer Programas tab: YouTube rankings scoped to digital channels only. */
export const EXPLORER_PROGRAMS_TAXONOMY = { channelType: "digital" as const };

/** Channel page opened from the digital ranking: TV channels show 100% digital programs. */
export const DIGITAL_PROGRAMS_QUERY = "digitalPrograms";

export function explorerPodcastPath(
  podcastId: string,
  opts?: { digitalPrograms?: boolean; clipsIa?: boolean },
): string {
  const path = opts?.clipsIa
    ? `/explorador/canales/ondemand/${podcastId}`
    : `/explorador/podcasts/${podcastId}`;
  if (!opts?.digitalPrograms) return path;
  return `${path}?${DIGITAL_PROGRAMS_QUERY}=1`;
}

export function explorerProgramPath(programId: string, clipsIa = false): string {
  return clipsIa
    ? `/explorador/canales/ondemand/programs/${programId}`
    : `/explorador/programs/${programId}`;
}

export function explorerSocialChannelPath(channelId: string, clipsIa = false): string {
  return clipsIa
    ? `/explorador/canales/clips/${channelId}`
    : `/explorador/clips/${channelId}`;
}

export function explorerLivestreamChannelPath(channelId: string, clipsIa = false): string {
  return clipsIa
    ? `/explorador/canales/livestreams/${channelId}`
    : `/explorador/livestreams/${channelId}`;
}

export function explorerLivestreamProgramPath(programId: string, clipsIa = false): string {
  return clipsIa
    ? `/explorador/canales/livestreams/program/${programId}`
    : `/explorador/livestreams/program/${programId}`;
}

export function explorerLivestreamCompetitionPath(clipsIa = false): string {
  return clipsIa
    ? `/explorador/canales/livestreams/rating`
    : `/explorador/livestreams/rating`;
}

/** Clips sponsor brand detail. Nested under Auspiciadores when Clips IA is on. */
export function explorerSocialBrandPath(brandId: string, clipsIa = false): string {
  return clipsIa
    ? `/explorador/auspiciadores/clips/${brandId}`
    : `/explorador/clips/brands/${brandId}`;
}

/** On Demand sponsor brand detail. Nested under Auspiciadores when Clips IA is on. */
export function explorerBrandPath(brandId: string, clipsIa = false): string {
  return clipsIa
    ? `/explorador/auspiciadores/ondemand/${brandId}`
    : `/explorador/brands/${brandId}`;
}

export function explorerBrandMoversPath(clipsIa = false): string {
  return clipsIa
    ? `/explorador/auspiciadores/ondemand/movers`
    : `/explorador/brands/movers`;
}

// Content categories for programs/podcasts filtering (must match backend contentCategories)
export const CONTENT_CATEGORIES = [
  "finance",
  "tech",
  "business",
  "health",
  "wellness",
  "entertainment",
  "music",
  "news",
  "politics",
  "sports",
  "lifestyle",
  "food",
  "education",
  "science",
  "culture",
  "true_crime",
  "gaming",
  "relationships",
  "other",
] as const;
export type ContentCategory = (typeof CONTENT_CATEGORIES)[number];

export const CONTENT_CATEGORY_LABELS: Record<string, string> = new Proxy(
  {} as Record<string, string>,
  {
    get(_target, prop) {
      if (typeof prop !== "string") return undefined;
      return i18n.t(`explorer:contentCategory.${prop}`);
    },
  },
);

// Livestream channel category type
export type LivestreamChannelCategory = "tv" | "noticias" | "radio" | "digital";

/** URL hash that scrolls the audience map to the day/hour rating block. */
export const LIVESTREAM_SLOT_RATING_HASH = "franja";

// Livestream channel country type
export type LivestreamChannelCountry = "cl" | "ar";

// Common props for tab components
export interface ExplorerTabProps {
  startDate: string;
  endDate: string;
}

/** Ranking tabs that own the period filter row (dates live in the tab). */
export interface ExplorerPeriodFilterTabProps extends ExplorerTabProps {
  onDateRangeChange: (startDate: string, endDate: string) => void;
  minDate: string;
  maxDate: string;
}

/** Shared height for Explorer filter-row controls (TextField/Select/Exportar/toggles). */
export const EXPLORER_FILTER_CONTROL_HEIGHT = 40;

/** Same width for every filter-row dropdown. The search field takes the remaining row width. */
export const EXPLORER_FILTER_SELECT_WIDTH = 176;
export const EXPLORER_FILTER_SELECT_MIN_WIDTH = EXPLORER_FILTER_SELECT_WIDTH;

/** Filter-row FormControl/TextField: fixed control height plus a min width. */
export function explorerFilterControlSx(
  minWidth: number = EXPLORER_FILTER_SELECT_MIN_WIDTH,
) {
  return {
    minWidth,
    "& .MuiInputBase-root": {
      height: EXPLORER_FILTER_CONTROL_HEIGHT,
      boxSizing: "border-box",
    },
  } as const;
}

/** Filter-row search box: fills the first column. */
export const EXPLORER_FILTER_SEARCH_SX = {
  width: "100%",
  minWidth: 0,
  "& .MuiInputBase-root": {
    height: EXPLORER_FILTER_CONTROL_HEIGHT,
    boxSizing: "border-box",
  },
} as const;

/** Looks like the filter-row dropdowns (outlined input), with their primary-border hover. */
export const EXPLORER_EXPORT_BUTTON_SX = {
  typography: "body1",
  height: EXPLORER_FILTER_CONTROL_HEIGHT,
  minHeight: EXPLORER_FILTER_CONTROL_HEIGHT,
  maxHeight: EXPLORER_FILTER_CONTROL_HEIGHT,
  py: 0,
  px: 1.75,
  boxSizing: "border-box",
  lineHeight: 1.25,
  border: 1,
  borderColor: "divider",
  borderRadius: "8px",
  bgcolor: "background.paper",
  color: "text.primary",
  transition: "all 0.2s ease-in-out",
  "& .MuiButton-startIcon": { color: "action.active" },
  "&:hover": {
    borderColor: "primary.main",
    bgcolor: "background.paper",
  },
  "&.Mui-disabled": {
    borderColor: "divider",
    color: "text.disabled",
  },
} as const;

// Props for Livestreams tab with optional category and country filters
export interface ExplorerLivestreamsTabProps extends ExplorerPeriodFilterTabProps {
  /** Empty is Todas. */
  categoryFilter?: LivestreamChannelCategory[];
  countryFilter: LivestreamChannelCountry; // Country is always required, no 'all' option
  /** Syncs with Explorer store; used by Comparador for an inline category control */
  onCategoryFilterChange?: (category: LivestreamChannelCategory[]) => void;
}

// Auspiciadores tab: category is controlled from ExplorerProduct (store) for locked categories
export interface ExplorerBrandsTabProps extends ExplorerPeriodFilterTabProps {
  categoryFilter: string;
  onCategoryFilterChange: (category: string) => void;
  /** When true, hide Agrupar por (company-locked category). */
  categoryFilterLocked?: boolean;
}
